import React, { useEffect, useState } from "react";
import {
  Spin,
  Button,
  Row,
  Col,
  Space,
  Card,
  Typography,
  Form,
  Modal,
  Input,
} from "antd";
import { ArrowRight, CircleAlert, GitFork, Search } from "lucide-react";

import { BRANCH_STORAGE, FONT } from "constants/index";
import { notificationErroInternal, notificationSucess } from "util/api-utils";
import { Columns, getBranchRowClassName } from "./columns-table";
import DrawerApp from "components/drawer-app";
import useBreakpoint from "hooks/useBreakpoint";
import ResponsiveDataList from "components/responsive-data-list";
import { drawerWidth, listMinWidth } from "util/responsive";
import BranchDrawerForm from "./branches-drawer-form";
import {
  deleteBranch,
  findAllBranches,
  putBranch,
  saveBranch,
} from "services/branch/branch-service";
import { useNavigate } from "react-router-dom";
import BranchStats from "./branches-stats";

const { Text } = Typography;
const { confirm } = Modal;

const getActiveBranchId = () => {
  try {
    const stored = localStorage.getItem(BRANCH_STORAGE);
    if (!stored) {
      return null;
    }
    const { id } = JSON.parse(stored);
    return id ?? null;
  } catch {
    return null;
  }
};

const BranchClient = () => {
  const { isMobile } = useBreakpoint();
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingDrawer, setLoadingDrawer] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState(null);
  const [activeBranchId, setActiveBranchId] = useState(getActiveBranchId);
  const navigate = useNavigate();

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = async () => {
    setLoadingTable(true);
    try {
      let obj = {
        search: search,
      };

      if (search == null || search == "") {
        obj = {};
      }
      const branchesData = await findAllBranches(obj);
      const branches = branchesData.map((item) => ({
        ...item,
        email: item.ownerName,
        edit,
        delete: disable,
        changeBranch,
      }));
      setData(branches);
      setActiveBranchId(getActiveBranchId());
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingTable(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoadingDrawer(true);
      const funcSave = editId !== null ? putBranch : saveBranch;
      await funcSave(editId, values);
      notificationSucess();
      onClose();
      refreshData();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingDrawer(false);
    }
  };

  const onClose = () => {
    setTimeout(() => {
      form.resetFields();
    }, 0);
    setEditId(null);
    setOpen(false);
    setLoadingDrawer(false);
  };

  function edit(row) {
    setEditId(row.id);
    setTimeout(() => {
      form.setFieldsValue({
        role: row.role,
        name: row.name,
        owner: row.owner,
        evoUser: row.evoUser,
        evoPass: row.evoPass,
        hasPhotocell: row.hasPhotocell,
        webhooks: row.webhooks,
      });
    }, 0);
    setOpen(true);
  }

  function disable(row) {
    confirm({
      title: (
        <div>
          <CircleAlert size={22} style={{ marginBottom: "-5px" }} />
          <span style={{ marginLeft: "10px", fontFamily: FONT }}>
            Tem certeza que deseja deletar a unidade '{row.name}' ?
          </span>
        </div>
      ),
      icon: null,
      okText: "Sim",
      okType: "danger",
      cancelText: "Não",
      onOk() {
        setLoadingAction(true);
        deleteBranch(row.id)
          .then(() => {
            notificationSucess();
            refreshData();
          })
          .catch(() => {
            notificationErroInternal(
              "Aconteceu um erro inesperado, por favor, tente mais tarde."
            );
          })
          .finally(() => {
            setLoadingAction(false);
          });
      },
      onCancel() {},
    });
  }

  const changeBranch = (row) => {
    setLoadingAction(true);
    localStorage.setItem(
      BRANCH_STORAGE,
      JSON.stringify({
        id: row.id,
        name: row.name,
        type: row.role,
        hasPhotocell: row.hasPhotocell,
      })
    );
    setActiveBranchId(row.id);
    setLoadingAction(false);
    navigate("/app/client/managerial");
  };

  const mountFilter = () => (
    <Row
      className="reviews-filter-row"
      gutter={[16, 16]}
      style={{ padding: "10px", alignItems: "center" }}
    >
      <Col xs={24} md={12} lg={10}>
        <Input
          allowClear
          value={search ?? ""}
          placeholder="Pesquisar Unidade"
          onChange={(e) => setSearch(e.target.value)}
          prefix={<Search size={18} color="#e0e0e0" />}
          style={{ width: "100%" }}
          onPressEnter={refreshData}
        />
      </Col>
      <Col xs={24} md={6} lg={4}>
        <Button
          style={{ fontFamily: FONT, width: "100%" }}
          type="primary"
          icon={
            <ArrowRight
              size={18}
              style={{ marginBottom: "-3px", marginRight: "7px" }}
            />
          }
          onClick={refreshData}
        >
          Filtrar
        </Button>
      </Col>
    </Row>
  );

  return (
    <div className="container-branches-client">
      <BranchStats values={data} />
      <Spin size="large" spinning={loadingTable || loadingAction}>
        {mountFilter()}
        <Row gutter={[24, 24]} style={{ marginLeft: "0px" }}>
          <Space
            direction="vertical"
            size={"small"}
            style={{
              flex: "1 1 500px",
              minWidth: listMinWidth(isMobile),
            }}
          >
            <Card
              id="card-table"
              className="card-table-list"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <GitFork
                      size={18}
                      style={{ marginBottom: "-4px", marginRight: "10px" }}
                    />
                    <Text style={{ fontSize: "15px", fontFamily: FONT }}>
                      Unidades
                    </Text>
                  </div>
                </div>
              }
              bordered={false}
            >
              <ResponsiveDataList
                isMobile={isMobile}
                forceCardLayout
                cardVariant="profile"
                rowKey="id"
                rowClassName={getBranchRowClassName(activeBranchId)}
                columns={Columns()}
                dataSource={data}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                locale={{
                  emptyText: "Sem registro",
                }}
              />
            </Card>
          </Space>
        </Row>
      </Spin>

      <DrawerApp
        open={open}
        onClose={onClose}
        title={editId ? "Editar Unidade" : "Nova Unidade"}
        body={
          <BranchDrawerForm
            form={form}
            editId={editId}
            loadingDrawer={loadingDrawer}
          />
        }
        size={isMobile ? "default" : "large"}
        width={drawerWidth(isMobile, 65)}
        actionSave={handleOk}
      />
    </div>
  );
};

export default BranchClient;
